import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Stack, Box, Typography, TextField, Button } from "@mui/material";
import { NumericFormat } from "react-number-format";

import type { ParentTitle } from "./CategoryInputsPanel";
import { Allowed } from "./MoneyFrequencyInput";
import { frequenciesKeys } from "../shared/utils";

interface InputRowProps {
  name: Allowed;
  label: string;
  parentTitle?: ParentTitle;
  maxValue?: number;
}

export function InputRow({
  name,
  label,
  parentTitle,
  maxValue,
}: Readonly<InputRowProps>) {
  const { control, setValue } = useFormContext<any>();

  const valueName = `${name}.value` as const;
  const freqName = `${name}.frequency` as const;
  const balanceName = `${name}.balance` as const;

  const clearValue = React.useCallback(() => {
    setValue(valueName, 0, { shouldDirty: true, shouldValidate: true });
  }, [setValue, valueName]);

  return (
    <>
      <Stack
        sx={{ mt: 2 }}
        spacing={{ md: 2 }}
        direction={{ xs: "column", md: "row" }}>
        <Box sx={{ width: { xs: "100%" }, borderRadius: "0px" }}>
          <Box paddingBottom={1}>
            <Typography fontWeight={"bold"} height={"20px"}>
              {label}
            </Typography>
          </Box>

          <Box sx={{ width: "100%", borderRadius: "0px" }}>
            <Controller
              control={control}
              name={valueName}
              rules={{ min: 0 }}
              render={({ field }) => {
                console.log(`InputRow ${label}:`, field.value);
                return (
                  <NumericFormat
                    style={{ width: "100%", backgroundColor: "#FFF" }}
                    customInput={TextField}
                    value={field.value ?? 0}
                    allowLeadingZeros={true}
                    decimalSeparator='.'
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator=','
                    prefix='£'
                    allowNegative={false}
                    isAllowed={(vals) =>
                      typeof maxValue === "number"
                        ? (vals.floatValue ?? 0) <= maxValue
                        : true
                    }
                    onValueChange={(vals) =>
                      field.onChange(vals.floatValue ?? 0)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                );
              }}
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <Button
              variant='outlined'
              color='error'
              fullWidth
              onClick={clearValue}>
              Clear Field
            </Button>
          </Box>
        </Box>

        <Box sx={{ width: { xs: "100%", md: "35%" } }}>
          <Controller
            control={control}
            name={freqName}
            render={({ field }) => (
              <TextField
                select
                label='Freq'
                fullWidth
                size='small'
                sx={{
                  mt: { xs: "8px", md: "28px" },
                  borderRadius: "0px",
                  backgroundColor: "#FFF",
                }}
                SelectProps={{ native: true }}
                inputProps={{ "data-testid": "freq-select" }}
                {...field}>
                {Object.keys(frequenciesKeys).map((f) => {
                  return (
                    <option key={f} value={f}>
                      {frequenciesKeys[f]}
                    </option>
                  );
                })}
              </TextField>
            )}
          />
        </Box>
      </Stack>

      {parentTitle === "Additional Debt" && (
        <Box sx={{ width: { xs: "100%", md: "63.2%" }, borderRadius: "0px" }}>
          <Box paddingY={1}>
            <Typography fontWeight={"bold"} height={"20px"}>
              {label} balance
            </Typography>
          </Box>
          <Box>
            <Controller
              control={control}
              name={balanceName}
              rules={{ min: 0 }}
              render={({ field }) => (
                <NumericFormat
                  style={{ width: "100%", backgroundColor: "#FFF" }}
                  customInput={TextField}
                  value={field.value ?? 0}
                  decimalSeparator='.'
                  decimalScale={2}
                  fixedDecimalScale
                  thousandSeparator=','
                  prefix='£'
                  allowNegative={false}
                  isAllowed={(vals) =>
                    typeof maxValue === "number"
                      ? Number(vals.value ?? 0) <= maxValue
                      : true
                  }
                  onValueChange={(vals) => field.onChange(vals.floatValue ?? 0)}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />
          </Box>
        </Box>
      )}
    </>
  );
}
